import React from "react";
import { T, useT } from "@transifex/react";
import {
  RiskResultsContainer,
  HeaderSection,
  Title,
  SubtitleText,
  ContentSection,
  StatBoxesContainer,
  StatBox,
  StatNumber,
  StatLabel,
  ActionsSection,
  ActionLink,
} from "./styles";

export type RiskStatus = "increased" | "decreased" | "average";

export interface RiskResultsCardProps {
  /** Card title - defaults to "Your Personalized Risk Assessment" */
  title?: string;
  /** Risk status - increased, decreased, or average (kept for backward compatibility) */
  status?: RiskStatus;
  /** Optional status label text (used as subtitle in new design) */
  statusLabel?: string;
  /** Optional status description text (used as subtitle in new design) */
  statusDescription?: string;
  /** Number of areas requiring action (elevated risk) */
  actionRequiredCount?: number;
  /** Number of areas on track (average risk) */
  onTrackCount?: number;
  /** Whether to show the Needs Attention / On Track counter boxes. Defaults to true. */
  showCounters?: boolean;
  /** Callback when download is clicked */
  onDownload?: () => void;
  /** Callback when share is clicked */
  onShare?: () => void;
  /** Callback when email-to-self is clicked. When undefined the link is hidden. */
  onEmail?: () => void;
  /** Optional className for additional styling */
  className?: string;
  /** Optional data-testid for testing */
  "data-testid"?: string;
}

const DownloadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6l-10 7L2 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function RiskResultsCard({
  title,
  statusDescription,
  actionRequiredCount = 0,
  onTrackCount = 0,
  showCounters = true,
  onDownload,
  onShare,
  onEmail,
  className,
  "data-testid": testId,
}: RiskResultsCardProps) {
  const t = useT();

  return (
    <RiskResultsContainer
      className={className}
      data-testid={testId || "RiskResultsCard"}
    >
      <HeaderSection data-testid="RiskResultsCard__Header">
        <Title data-testid="RiskResultsCard__Title">
          {title || <T _str="Your Personalized Risk Assessment" />}
        </Title>
        {statusDescription && (
          <SubtitleText data-testid="RiskResultsCard__Subtitle">
            {statusDescription}
          </SubtitleText>
        )}
      </HeaderSection>

      {showCounters && (
        <ContentSection data-testid="RiskResultsCard__Content">
          <StatBoxesContainer data-testid="RiskResultsCard__StatBoxes">
            <StatBox $type="action" data-testid="RiskResultsCard__ActionBox">
              <StatNumber $type="action">{actionRequiredCount}</StatNumber>
              <StatLabel $type="action">
                <T _str="Needs Attention" />
              </StatLabel>
            </StatBox>
            <StatBox $type="ontrack" data-testid="RiskResultsCard__OnTrackBox">
              <StatNumber $type="ontrack">{onTrackCount}</StatNumber>
              <StatLabel $type="ontrack">
                <T _str="On Track" />
              </StatLabel>
            </StatBox>
          </StatBoxesContainer>
        </ContentSection>
      )}

      <ActionsSection data-testid="RiskResultsCard__Actions">
        <ActionLink
          onClick={onDownload}
          data-testid="RiskResultsCard__DownloadLink"
        >
          <DownloadIcon />
          <T _str="Download" />
        </ActionLink>
        {onEmail && (
          <ActionLink
            onClick={onEmail}
            data-testid="RiskResultsCard__EmailLink"
          >
            <EmailIcon />
            <T _str="Email" />
          </ActionLink>
        )}
        <ActionLink
          onClick={onShare}
          data-testid="RiskResultsCard__ShareLink"
        >
          <ShareIcon />
          <T _str="Share" />
        </ActionLink>
      </ActionsSection>
    </RiskResultsContainer>
  );
}
